import '../styles/components/TravelDate.css'
import type {SearchFilters, Termin, TooltipTextsProps} from "../types/travel.ts";
import {Pill} from "./Pill.tsx";
import {AccommodationIcon} from "./AccommodationIcon.tsx";
import {DifficultyLevels} from "./DifficultyLevels.tsx";
import {Availability} from "./Availability.tsx";
import {formatGermanPrice} from "../utils/priceFormatter";
import {decodeHtmlEntities} from "../utils/htmlUtils.ts";
import {trackEvent} from "../utils/analytics.ts";
import {setCookie} from "../utils/cookies.ts";
import {accommodationItemsMapping} from "../utils/accommodationMapping.ts";
import {toYearMonthDayTrackingString, truncateToMaxBytes} from "../utils/general.ts";

interface TerminCardProps {
    termin: Termin
    toolTipTexts?: TooltipTextsProps
    filters?: SearchFilters,
    terminTotal: number,
}

export function TerminCard({termin, toolTipTexts, filters, terminTotal}: TerminCardProps) {
    const {
        id,
        extid,
        title,
        preis,
        dauer,
        //hauptland,
        difficulty,
        difficultyValue = 0,
        fromPrice,
        url,
        unterkunft,
        kontinent,
        flug,
        anreise,
        rueckreise,
        status,
        reiseart,
        hauptregionen
    } = termin

    // Format date from YYYYMMDD to German format
    const formatGermanDate = (dateString: string): string => {
        if (!dateString || dateString.length !== 8) return dateString;

        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);

        const monthNames = [
            'Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni',
            'Juli', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'
        ];

        const monthName = monthNames[parseInt(month) - 1];
        return `${parseInt(day)}. ${monthName}`;
    };

    // Format date range
    const formatDateRange = (anreise: string, rueckreise: string) => {
        if (!anreise || !rueckreise) return '';

        const formattedAnreise = formatGermanDate(anreise);
        const formattedRueckreise = formatGermanDate(rueckreise);
        const year = rueckreise.substring(0, 4);

        return <>{formattedAnreise} - <br/>{formattedRueckreise} {year}</>;
    };

    const getBookingButtonParameters = (type: number) => {
        let parameters = {
            text : 'Buchen',
            disabled: 'is-disabled',
            cssClass: 'green'
        };

        if(type === 1) {
            parameters = {
                text : 'Buchen',
                disabled: '',
                cssClass: 'green'
            };
        }

        if(type === 2) {
            parameters = {
                text : 'Buchen',
                disabled: '',
                cssClass: 'green'
            };
        }

        if(type === 3) {
            parameters = {
                text : 'Buchen',
                disabled: '',
                cssClass: 'yellow'
            };
        }

        if(type === 4) {
            parameters = {
                text : 'Buchen',
                disabled: 'is-disabled',
                cssClass: 'disabled'
            };
        }

        if(type === 5) {
            parameters = {
                text : 'Buchen',
                disabled: 'is-disabled',
                cssClass: 'black'
            };
        }

        return parameters;
    }

    const bookingUrl = `/buchen/?extid_reise=${extid}&extid_termin=${id}`;

    const colorClass = "region-" + (kontinent && kontinent[0] ? kontinent[0].toLowerCase() : "");



    const handleButtonClick = (e: MouseEvent | TouchEvent, type: string) => {
        e.preventDefault();
        const target = e.currentTarget as HTMLAnchorElement | null;

        let trackDestinationData = filters?.destination ?? [];
        let trackDestinationString = '';
        if(trackDestinationData.length > 0) {
            trackDestinationString = trackDestinationData.join(',');
        }

        let trackSearchDates = 'egal';
        let trackDestinationYearMonth = filters?.yearmonth ?? [];
        if(trackDestinationYearMonth.length > 0) {
            trackSearchDates = trackDestinationYearMonth.map((item) => toYearMonthDayTrackingString(item))
                .join(',');
        }

        let trackDestinationStart = filters?.start ?? '';
        let trackDestinationEnd = filters?.end ?? '';
        if(trackDestinationStart.length > 0 && trackDestinationEnd.length > 0) {
            const trackingStart = toYearMonthDayTrackingString(trackDestinationStart);
            const trackingEnd = toYearMonthDayTrackingString(trackDestinationEnd);
            trackSearchDates = `${trackingStart} bis ${trackingEnd}`;
        }

        const tracking_options = {
            type: type,
            outbound: false,
            search_term: truncateToMaxBytes(trackDestinationString),
            search_category: 'reisekalender',
            search_datetime: truncateToMaxBytes(trackSearchDates),
            search_results: terminTotal || 0
        }

        trackEvent('click', tracking_options);

        if(type === 'buchen') {
            setCookie('ep-search-track-reisekalender', JSON.stringify(tracking_options), 1);
        }


        if(target) {
            const href = target.href;
            if(href) {
                window.location.href = href;
            }
        }
    }

    return (
        <div className="travel-date" data-id={id}>
            <div className="travel-date-inner">

                <div className="col col-1">
                    <div className="meta">
                        {hauptregionen && (
                            <>
                                <div className="main-country flex gap-1 flex-wrap lg:flex-nowrap">
                                    {hauptregionen?.data?.map(item => (
                                        <Pill
                                            title={item.name}
                                            colorClass={item.color_class}
                                            targetUrl={item.landingpage_url}
                                        />
                                    ))}

                                    {hauptregionen?.regionen_count_gt_1 && (
                                        <Pill
                                            title={`+ ${hauptregionen.regionen_count_gt_1}`}
                                            colorClass={colorClass}
                                        />
                                    )}
                                </div>

                                {accommodationItemsMapping(unterkunft ?? []).map(item => {
                                    const toolTipText = toolTipTexts?.travel && toolTipTexts?.travel[item ?? 'Hotel'] ? toolTipTexts?.travel[item ?? 'Hotel'] : '';
                                    return (
                                    <AccommodationIcon
                                        icon={item ?? 'Hotel'}
                                        colorClass={colorClass}
                                        toolTipText={toolTipText}
                                    />)
                                })}
                            </>
                        )}
                    </div>

                    <div className="h3"><a href={url}>{decodeHtmlEntities(title)}</a></div>

                    <div className="h5">{dauer} TAGE {reiseart?.includes("Familienreise") ? `FAMILIENREISE` : `WANDERREISE`}</div>
                </div>

                <div className="col col-2">
                    {/* Todo Vinz also half difficulties are possible*/}
                    <DifficultyLevels
                        difficulty={difficulty}
                        difficultyValue={difficultyValue}
                        variant={0}
                    />
                </div>

                <div className="col col-4">
                    <div className="date">{formatDateRange(anreise || '', rueckreise || '')}</div>
                    <Availability type={status}/>
                </div>

                <div className="col col-5">
                    <div className="price">€ {formatGermanPrice(fromPrice || preis)}</div>
                    <div className="flug">/ Person, {flug && `${flug}`}</div>
                </div>

                <div className="col col-6">
                    <a href={url} className="btn ghost height-40 event-tracking-reisekalender" onClick={(e) => handleButtonClick(e,'details')}>Details</a>
                    <div className="book-now">
                        <a href={bookingUrl} onClick={(e) => handleButtonClick(e, 'buchen')} className={`
                            btn height-40 event-tracking-reisekalender 
                            ${getBookingButtonParameters(status ?? 4).cssClass}
                            ${getBookingButtonParameters(status ?? 4).disabled}
                        `}>
                            {getBookingButtonParameters(status ?? 4).text}</a>
                    </div>
                </div>

            </div>
        </div>
    );

}
